/**
 * SCHEMA ÜRETİCİ — form tabanlı, canlı önizlemeli.
 *
 * Merkle'ın schema-markup-generator aracının yaptığı iş: tip seç, alanları doldur,
 * JSON-LD anında oluşsun. Sayfadan AI ile üretmekten AYRI bir araç — kullanıcının
 * elindeki bilgi (fiyat, çalışma saati, etkinlik tarihi) sayfada yazmıyor olabilir.
 *
 * Boş alanlar çıktıya HİÇ girmez: boş string bırakmak schema'yı kirletir ve
 * Google'da "eksik değer" olarak okunur.
 */
import { useMemo, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Copy, Check, Plus, Trash2, ExternalLink } from "lucide-react";
import { SCHEMA_FORMLARI, jsonLdKur, eksikZorunlular, type Alan, type SchemaFormu } from "@/lib/schema-formlari";

type Degerler = Record<string, unknown>;

function AlanGirisi({ alan, deger, degistir }: { alan: Alan; deger: unknown; degistir: (v: unknown) => void }) {
  const ortak = "w-full text-sm px-2.5 py-1.5 rounded-lg border bg-background";
  const etiket = (
    <label className="block text-[11px] font-medium mb-1">
      {alan.etiket}
      {alan.zorunlu && <span className="text-error-500 ml-0.5" title="Google zengin sonuç için zorunlu">*</span>}
    </label>
  );

  // ── Tekrarlanabilir liste ──
  if (alan.tur === "liste") {
    const satirlar = (Array.isArray(deger) ? deger : []) as Record<string, unknown>[];
    const gorunen: Record<string, unknown>[] = satirlar.length
      ? satirlar
      : Array.from({ length: alan.enAz ?? 1 }, () => ({} as Record<string, unknown>));
    const yaz = (yeni: Record<string, unknown>[]) => degistir(yeni);
    return (
      <div className="col-span-full">
        {etiket}
        {alan.ipucu && <p className="text-[10px] text-muted-foreground mb-1.5">{alan.ipucu}</p>}
        <div className="space-y-2">
          {gorunen.map((satir, i) => (
            <div key={i} className="flex gap-2 items-start">
              <span className="text-[10px] text-muted-foreground w-4 pt-2 shrink-0 tabular-nums">{i + 1}</span>
              <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-2">
                {(alan.alanlar ?? []).map((alt) => (
                  <AlanGirisi key={alt.ad} alan={alt} deger={satir[alt.ad]}
                    degistir={(v) => { const k = [...gorunen]; k[i] = { ...k[i], [alt.ad]: v }; yaz(k); }} />
                ))}
              </div>
              <button onClick={() => yaz(gorunen.filter((_, j) => j !== i))} title="Satırı sil"
                className="p-1.5 mt-0.5 rounded text-muted-foreground hover:text-brand-600 shrink-0">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
        <Button size="sm" variant="outline" className="mt-2 h-7 text-[11px]" onClick={() => yaz([...gorunen, {}])}>
          <Plus className="w-3 h-3 mr-1" /> Satır ekle
        </Button>
      </div>
    );
  }

  // ── Gömülü nesne ──
  if (alan.tur === "nesne") {
    const ic = (deger ?? {}) as Record<string, unknown>;
    return (
      <div className="col-span-full rounded-lg border border-border p-3">
        {etiket}
        {alan.ipucu && <p className="text-[10px] text-muted-foreground mb-2">{alan.ipucu}</p>}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {(alan.alanlar ?? []).map((alt) => (
            <AlanGirisi key={alt.ad} alan={alt} deger={ic[alt.ad]}
              degistir={(v) => degistir({ ...ic, [alt.ad]: v })} />
          ))}
        </div>
      </div>
    );
  }

  const v = typeof deger === "string" || typeof deger === "number" ? String(deger) : "";
  return (
    <div>
      {etiket}
      {alan.tur === "uzunMetin" ? (
        <textarea value={v} onChange={(e) => degistir(e.target.value)} rows={2} placeholder={alan.ornek} className={`${ortak} resize-y`} />
      ) : alan.tur === "secim" ? (
        <select value={v} onChange={(e) => degistir(e.target.value)} className={ortak}>
          <option value="">— seçin —</option>
          {(alan.secenekler ?? []).map((s) => <option key={s} value={s}>{s.replace("https://schema.org/", "")}</option>)}
        </select>
      ) : (
        <input
          value={v} onChange={(e) => degistir(e.target.value)} placeholder={alan.ornek}
          type={alan.tur === "tarih" ? "date" : alan.tur === "tarihSaat" ? "datetime-local" : alan.tur === "sayi" ? "number" : "text"}
          className={ortak}
        />
      )}
      {alan.ipucu && <p className="text-[10px] text-muted-foreground mt-0.5">{alan.ipucu}</p>}
    </div>
  );
}

export function SchemaUretici() {
  const [tip, setTip] = useState(SCHEMA_FORMLARI[0]!.tip);
  const [degerler, setDegerler] = useState<Record<string, Degerler>>({});
  const [kopyalandi, setKopyalandi] = useState(false);

  const form = useMemo<SchemaFormu>(() => SCHEMA_FORMLARI.find((f) => f.tip === tip) ?? SCHEMA_FORMLARI[0]!, [tip]);
  const mevcut: Degerler = degerler[tip] ?? {};
  const jsonLd = useMemo(() => JSON.stringify(jsonLdKur(form, mevcut), null, 2), [form, mevcut]);
  const eksik = useMemo(() => eksikZorunlular(form, mevcut), [form, mevcut]);
  const script = `<script type="application/ld+json">\n${jsonLd}\n</script>`;

  return (
    <div className="grid lg:grid-cols-2 gap-4">
      {/* ── FORM ── */}
      <Card><CardContent className="p-4">
        <label className="block text-[11px] font-medium mb-1">Schema Tipi</label>
        <select value={tip} onChange={(e) => setTip(e.target.value)} className="w-full text-sm px-2.5 py-2 rounded-lg border bg-background mb-1">
          {SCHEMA_FORMLARI.map((f) => <option key={f.tip} value={f.tip}>{f.ad} ({f.tip})</option>)}
        </select>
        <p className="text-[11px] text-muted-foreground mb-3">{form.aciklama}</p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {form.alanlar.map((a) => (
            <AlanGirisi key={a.ad} alan={a} deger={mevcut[a.ad]}
              degistir={(v) => setDegerler((p) => ({ ...p, [tip]: { ...(p[tip] ?? {}), [a.ad]: v } }))} />
          ))}
        </div>

        <Button size="sm" variant="ghost" className="mt-3 text-[11px]" onClick={() => setDegerler((p) => ({ ...p, [tip]: {} }))}>
          Formu temizle
        </Button>
      </CardContent></Card>

      {/* ── CANLI ÇIKTI ── */}
      <div className="space-y-3 lg:sticky lg:top-4 lg:self-start">
        <Card><CardContent className="p-4">
          <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
            <span className="font-semibold text-sm">Canlı JSON-LD</span>
            <Button size="sm" variant="outline"
              onClick={() => { void navigator.clipboard.writeText(script); setKopyalandi(true); setTimeout(() => setKopyalandi(false), 2000); }}>
              {kopyalandi ? <><Check className="w-3.5 h-3.5 mr-1" /> Kopyalandı</> : <><Copy className="w-3.5 h-3.5 mr-1" /> Script olarak kopyala</>}
            </Button>
          </div>

          {eksik.length > 0 ? (
            <div className="mb-2 text-[12px] rounded-lg px-3 py-2 bg-warning-50 text-warning-700 dark:bg-warning-500/15 dark:text-warning-400">
              Zorunlu alan eksik: <b>{eksik.join(", ")}</b> — bu haliyle Google zengin sonuç vermez.
            </div>
          ) : (
            <div className="mb-2 text-[12px] rounded-lg px-3 py-2 bg-success-50 text-success-700 dark:bg-success-500/15 dark:text-success-400">
              Google zorunlu alanları tamam.
            </div>
          )}

          <pre className="text-[11px] bg-accent rounded-lg p-3 overflow-x-auto max-h-[460px]">{jsonLd}</pre>

          <p className="text-[10px] text-muted-foreground mt-2">
            Bu kodu sayfanın <code>&lt;head&gt;</code> bölümüne yapıştırın.
          </p>
          <div className="flex gap-3 mt-1.5 flex-wrap">
            <a href="https://search.google.com/test/rich-results" target="_blank" rel="noreferrer"
              className="text-[11px] underline text-muted-foreground inline-flex items-center gap-1">
              Google Zengin Sonuç Testi <ExternalLink className="w-3 h-3" />
            </a>
            <a href="https://validator.schema.org/" target="_blank" rel="noreferrer"
              className="text-[11px] underline text-muted-foreground inline-flex items-center gap-1">
              schema.org Doğrulayıcı <ExternalLink className="w-3 h-3" />
            </a>
          </div>
        </CardContent></Card>
      </div>
    </div>
  );
}
